/**
 * CollisionSafetyMonitor
 * Detects and warns about common collision failure scenarios
 *
 * GEOMETRY: this monitor exists to report when the engine's collision state
 * disagrees with reality, so it MUST measure bodies exactly the way the engine
 * does. Every bound here therefore comes from `PhysicsUtils` — the same
 * `getCharacterBounds`/`getPlatformBounds`/`isColliding` that `CollisionResolver`
 * and `RopePhysicsSystem` resolve against.
 *
 * It used to carry private copies instead, and the character copy hardcoded a
 * 40x40 box that ignored `character.width`/`character.height` (DEBUG-GEOM-D4,
 * fixed 2026-07-27). A watchdog judging the engine against bounds the engine
 * never uses can only ever produce false verdicts in both directions — the same
 * two-places-one-fact drift class COMBAT-MELEE-CONE-03/04 closed in combat (see
 * `src/shared/logic/CombatAnchors.ts`). Do not reintroduce a local bounds
 * helper here.
 */

import { Helix2000Character, Platform } from '../../shared/CharacterTypes';
import { browserLogger } from '../logging/BrowserLogger';
import { gameLogger } from '../../utils/GameLogger';
import { PhysicsUtils } from '../systems/physics/PhysicsUtils';
import { Bounds } from '../systems/physics/types/CollisionTypes';

export interface CollisionFailure {
  type: 'no-collision-detected' | 'falling-through-floor' | 'stuck-in-platform' | 'velocity-not-reset' | 'grounded-state-wrong';
  severity: 'warning' | 'error';
  message: string;
  characterId: string;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  timestamp: number;
  suggestions: string[];
}

/**
 * Whether this monitor has any producer at all, and how much it has actually
 * seen. An empty `failureBuffer` means nothing without `wired` alongside it.
 */
export interface CollisionMonitorStatus {
  /** True once at least one character has actually been measured. */
  wired: boolean;
  /** Characters measured since page load. Monotonic — `clearFailures` does not reset it. */
  observations: number;
  /** Failures currently held in the ring buffer. */
  failures: number;
}

export class CollisionSafetyMonitor {
  private static instance: CollisionSafetyMonitor;
  private failureBuffer: CollisionFailure[] = [];
  private readonly maxFailures = 20;
  private lastWarningTime = 0;
  private warningCooldown = 2000; // Don't spam warnings

  /**
   * Characters actually measured. An empty failure buffer is only evidence of
   * health when this is non-zero — otherwise it just means nobody is feeding
   * the monitor, which is the live situation (DEBUG-SAFETY-WIRE-01): the only
   * caller `monitorCharacter` ever had was the Phaser-era `PhysicsSystem`,
   * deleted in 97d7e42b. Deliberately monotonic: clearing the failure LOG is a
   * statement about the log, not evidence that the producer went away.
   */
  private observations = 0;

  // Monitoring state
  private characterStates = new Map<string, {
    lastPosition: { x: number; y: number };
    lastVelocity: { x: number; y: number };
    framesBelowFloor: number;
    framesWithoutCollision: number;
    lastGroundedState: boolean;
  }>();

  private constructor() {
    this.setupCollisionMonitoring();
  }

  public static getInstance(): CollisionSafetyMonitor {
    if (!CollisionSafetyMonitor.instance) {
      CollisionSafetyMonitor.instance = new CollisionSafetyMonitor();
    }
    return CollisionSafetyMonitor.instance;
  }

  /**
   * Monitor character for collision failures
   */
  public monitorCharacter(
    character: Helix2000Character,
    platforms: Platform[],
    _deltaTime: number
  ): void {
    if (!character || !character.position || !character.velocity) return;

    // Counted only past the validity guard: a call that bailed measured nothing.
    this.observations++;

    const charId = character.id || 'unknown';
    let state = this.characterStates.get(charId);
    
    if (!state) {
      state = {
        lastPosition: { ...character.position },
        lastVelocity: { ...character.velocity },
        framesBelowFloor: 0,
        framesWithoutCollision: 0,
        lastGroundedState: character.isGrounded
      };
      this.characterStates.set(charId, state);
    }

    // Check 1: Character falling below floor
    if (character.position.y > 600) { // Below typical floor level
      state.framesBelowFloor++;
      
      if (state.framesBelowFloor > 30) { // Falling for more than 0.5 seconds
        this.reportFailure({
          type: 'falling-through-floor',
          severity: 'error',
          message: `Character ${charId} is falling through the floor!`,
          characterId: charId,
          position: { ...character.position },
          velocity: { ...character.velocity },
          timestamp: Date.now(),
          suggestions: [
            'Check platform collision is enabled',
            'Verify platform bounds are correct',
            'Check PhysicsCoordinator collision detection logic',
            'Enable hitbox visualization to see bounds'
          ]
        });
      }
    } else {
      state.framesBelowFloor = 0;
    }

    // Check 2: High downward velocity but not colliding
    if (character.velocity.y > 200 && !character.isGrounded) { // Falling fast
      const shouldBeColliding = this.checkShouldBeColliding(character, platforms);
      
      if (!shouldBeColliding) {
        state.framesWithoutCollision++;
        
        if (state.framesWithoutCollision > 10) {
          this.reportFailure({
            type: 'no-collision-detected',
            severity: 'error',
            message: `Character ${charId} falling fast but no collision detected!`,
            characterId: charId,
            position: { ...character.position },
            velocity: { ...character.velocity },
            timestamp: Date.now(),
            suggestions: [
              'Enable hitbox visualization to see if bounds overlap',
              'Check platform collision bounding boxes',
              'Verify collision detection is running',
              'Check for early returns in collision logic'
            ]
          });
        }
      } else {
        state.framesWithoutCollision = 0;
      }
    }

    // Check 3: Grounded state doesn't match reality
    const charBounds = PhysicsUtils.getCharacterBounds(character);
    const isActuallyGrounded = this.checkIfActuallyGrounded(charBounds, platforms);
    
    if (character.isGrounded !== isActuallyGrounded) {
      this.reportFailure({
        type: 'grounded-state-wrong',
        severity: 'warning',
        message: `Character ${charId} grounded state mismatch! Character thinks it's ${character.isGrounded ? 'grounded' : 'airborne'} but actually ${isActuallyGrounded ? 'grounded' : 'airborne'}`,
        characterId: charId,
        position: { ...character.position },
        velocity: { ...character.velocity },
        timestamp: Date.now(),
        suggestions: [
          'Check grounded state detection logic',
          'Verify platform bounds for ground check',
          'Check offset values in ground detection',
          'Enable physics state indicators to see live status'
        ]
      });
    }

    // Update state
    state.lastPosition = { ...character.position };
    state.lastVelocity = { ...character.velocity };
    state.lastGroundedState = character.isGrounded;
  }

  /**
   * Check if character should be colliding with platforms
   */
  private checkShouldBeColliding(character: Helix2000Character, platforms: Platform[]): boolean {
    if (!character.position) return false;
    
    const charBounds = PhysicsUtils.getCharacterBounds(character);

    return platforms.some(platform => {
      const platformBounds = PhysicsUtils.getPlatformBounds(platform);

      // Check if character is within reasonable distance above platform
      const isAbove = charBounds.bottom < platformBounds.top + 50;
      const isCloseHorizontally = charBounds.right > platformBounds.left && charBounds.left < platformBounds.right;
      
      return isAbove && isCloseHorizontally;
    });
  }

  /**
   * Check if character is actually grounded based on position
   */
  private checkIfActuallyGrounded(charBounds: Bounds, platforms: Platform[]): boolean {
    const checkBounds: Bounds = {
      left: charBounds.left + 5,
      right: charBounds.right - 5,
      top: charBounds.bottom,
      bottom: charBounds.bottom + 5
    };

    return platforms.some(platform =>
      PhysicsUtils.isColliding(checkBounds, PhysicsUtils.getPlatformBounds(platform))
    );
  }

  /**
   * Report a collision failure
   */
  private reportFailure(failure: CollisionFailure): void {
    const now = Date.now();
    
    // Prevent spam
    if (now - this.lastWarningTime < this.warningCooldown) return;
    this.lastWarningTime = now;

    // Add to buffer
    this.failureBuffer.push(failure);
    if (this.failureBuffer.length > this.maxFailures) {
      this.failureBuffer.shift();
    }

    // Log with clear visual indicator
    browserLogger.error(failure.message, {
      type: failure.type,
      characterId: failure.characterId,
      position: failure.position,
      velocity: failure.velocity,
      suggestions: failure.suggestions
    });

    // Log collision failure with gameLogger
    gameLogger.warn('debug', `Collision failure: ${failure.type} - ${failure.message}`, {
      type: failure.type,
      characterId: failure.characterId,
      position: failure.position,
      velocity: failure.velocity,
      suggestions: failure.suggestions
    });
  }

  /**
   * Setup collision event monitoring
   */
  private setupCollisionMonitoring(): void {
    // This will subscribe to collision debugger events
    // if more detailed monitoring is needed
  }

  /**
   * Get recent failures
   */
  public getRecentFailures(count: number = 10): CollisionFailure[] {
    return this.failureBuffer.slice(-count);
  }

  /**
   * Whether anything is feeding this monitor, so a reader can tell "measured,
   * found nothing" apart from "measured nothing". Any surface that renders
   * `getRecentFailures()` MUST consult this before calling an empty list a pass.
   */
  public getStatus(): CollisionMonitorStatus {
    return {
      wired: this.observations > 0,
      observations: this.observations,
      failures: this.failureBuffer.length,
    };
  }

  /**
   * Clear failure buffer
   */
  public clearFailures(): void {
    this.failureBuffer = [];
    this.characterStates.clear();
  }

  /**
   * Get failure statistics
   */
  public getStatistics() {
    return {
      totalFailures: this.failureBuffer.length,
      failureTypes: [...new Set(this.failureBuffer.map(f => f.type))],
      bySeverity: {
        errors: this.failureBuffer.filter(f => f.severity === 'error').length,
        warnings: this.failureBuffer.filter(f => f.severity === 'warning').length
      }
    };
  }
}

// Singleton and convenience functions
export const collisionSafetyMonitor = CollisionSafetyMonitor.getInstance();

export const monitorCharacterCollision = (
  character: Helix2000Character,
  platforms: Platform[],
  deltaTime: number
) => {
  collisionSafetyMonitor.monitorCharacter(character, platforms, deltaTime);
};

export const getCollisionFailures = () => {
  return collisionSafetyMonitor.getRecentFailures();
};

export const getCollisionMonitorStatus = (): CollisionMonitorStatus => {
  return collisionSafetyMonitor.getStatus();
};

export const clearCollisionFailures = () => {
  collisionSafetyMonitor.clearFailures();
};
